// ============================================================================
// MOCK DATA — Replace with real API responses later
// ============================================================================

export const MOCK_POSTS = [
  {
    id: "featured-1",
    type: "analysis",
    pinned: true,
    title: "UFT Contract Analysis: 18 Years of Eroding Purchasing Power",
    summary:
      "A deep-dive into how NYC teacher salaries have failed to keep pace with inflation since 2009. Interactive charts, three salary profiles, and the real numbers the DOE doesn't show you.",
    tags: ["UFT", "Salary", "Inflation", "NYC DOE"],
    component: "contract-analysis",
    author: "wyzcon",
    timestamp: "2025-02-18T10:00:00Z",
    sources: [
      {
        name: "UFT-DOE Collective Bargaining Agreement 2022-2027",
        publisher: "United Federation of Teachers",
        type: "primary",
        url: "https://www.uft.org/your-rights/your-contract",
        date: "Sept 2023",
        accessed: "Feb 15, 2025",
        summary: "Used for all contractual raise percentages and effective dates from 2009-2027. Salary schedules referenced to calculate nominal pay at each step/differential level across the three teacher profiles.",
        dataPoints: ["Raise %s by year", "Salary schedules 1A/8B/22yr", "Effective dates"],
      },
      {
        name: "Consumer Price Index — New York-Newark-Jersey City",
        publisher: "Bureau of Labor Statistics (BLS)",
        type: "primary",
        url: "https://www.bls.gov/regions/new-york-new-jersey/news-release/consumerpriceindex_newyorkarea.htm",
        date: "Jan 2025",
        accessed: "Feb 16, 2025",
        summary: "NYC metro area CPI-U annual averages used to calculate real (inflation-adjusted) salary values. Each year's CPI change was applied to compute purchasing power erosion against nominal contract raises.",
        dataPoints: ["Annual CPI-U 2009-2024", "NYC metro area figures"],
      },
      {
        name: "StreetEasy Median Asking Rent Report",
        publisher: "StreetEasy / Zillow Group",
        type: "secondary",
        url: "https://streeteasy.com/blog/data-dashboard/",
        date: "Q4 2024",
        accessed: "Feb 14, 2025",
        summary: "Referenced for contextual housing cost data to illustrate the gap between teacher salary growth and NYC rent increases. Not used in core salary calculations, but cited in the narrative framing of purchasing power loss.",
        dataPoints: ["Median rent trends 2009-2024"],
      },
    ],
    reactions: { wise: 47, insightful: 32, fire: 89, data: 55, frustrated: 28 },
    comments: [
      {
        id: "c1",
        author: "a1x",
        text: "Finally someone put the actual numbers together. I've been saying this for years but nobody listens.",
        ts: "2h ago",
        created_at: "2025-02-18T08:00:00Z",
      },
      {
        id: "c2",
        author: "b2y",
        text: "The Bloomberg years were brutal. 5 years with 0% while rent kept climbing.",
        ts: "1h ago",
        created_at: "2025-02-18T09:00:00Z",
      },
      {
        id: "c3",
        author: "c3z",
        text: "Can you add a comparison to NYPD/FDNY contract raises? Would love to see that side by side.",
        ts: "45m ago",
        created_at: "2025-02-18T09:15:00Z",
      },
    ],
  },
  {
    id: "news-1",
    type: "news",
    title: "NYC DOE Announces $2.2B Budget Shortfall — What It Means for Schools",
    summary:
      "The Department of Education faces its largest deficit in a decade. Class sizes, support staff, and program funding all at risk.",
    tags: ["Budget", "NYC DOE", "Policy"],
    author: "wyzcon",
    timestamp: "2025-02-17T14:30:00Z",
    sources: [
      {
        name: "NYC DOE FY2026 Preliminary Budget Overview",
        publisher: "NYC Department of Education",
        type: "primary",
        url: "https://www.schools.nyc.gov/about-us/funding/budget",
        date: "Feb 2025",
        accessed: "Feb 17, 2025",
        summary: "Official budget documents showing projected revenue vs. expenditures. The $2.2B shortfall figure comes directly from the DOE's own preliminary budget presentation to the Panel for Educational Policy.",
        dataPoints: ["$2.2B deficit figure", "Line-item breakdowns"],
      },
      {
        name: "Independent Budget Office Fiscal Brief",
        publisher: "NYC Independent Budget Office (IBO)",
        type: "primary",
        url: "https://www.ibo.nyc.ny.us/",
        date: "Feb 2025",
        accessed: "Feb 17, 2025",
        summary: "IBO's independent analysis confirming the DOE shortfall projection and providing context on how hiring freezes and program cuts would be distributed across school districts. Used for the impact projections listed in this post.",
        dataPoints: ["Hiring freeze impact", "Program cut distribution"],
      },
    ],
    content: `<p>The NYC Department of Education announced a projected <strong>$2.2 billion budget shortfall</strong> for the upcoming fiscal year, marking the largest deficit since 2008.</p><p>Key impacts to watch:</p><ul style="color:#8b86b0;padding-left:20px"><li style="margin-bottom:8px">Potential increase in class sizes as hiring freezes take effect</li><li style="margin-bottom:8px">Reduction in after-school and enrichment programs</li><li style="margin-bottom:8px">Social worker and counselor positions at risk</li><li style="margin-bottom:8px">Technology refresh cycles delayed 2+ years</li></ul><p style="color:#a78bfa;font-style:italic">Wyzcon Intelligence will continue monitoring this situation and provide analysis as budget details emerge.</p>`,
    reactions: { wise: 12, frustrated: 45, insightful: 8 },
    comments: [
      {
        id: "c4",
        author: "d4w",
        text: "My school already lost 3 positions this year. When does it end?",
        ts: "5h ago",
        created_at: "2025-02-17T09:30:00Z",
      },
      {
        id: "c5",
        author: "e5v",
        text: "The enrichment programs are what keep kids engaged. Cutting those is short-sighted.",
        ts: "3h ago",
        created_at: "2025-02-17T11:30:00Z",
      },
    ],
  },
  {
    id: "analysis-2",
    type: "analysis",
    title: "Teacher Retention Crisis: NYC Losing 30% of New Teachers Within 3 Years",
    summary:
      "Our analysis of DOE workforce data reveals an accelerating attrition crisis. The numbers, the patterns, and what's really driving teachers out.",
    tags: ["Retention", "Workforce", "Research"],
    author: "wyzcon",
    timestamp: "2025-02-15T09:00:00Z",
    sources: [
      {
        name: "NYC DOE Workforce Demographics & Attrition Reports 2018-2024",
        publisher: "NYC Department of Education — Division of Human Resources",
        type: "primary",
        url: "https://infohub.nyced.org/reports/school-quality/teacher-data",
        date: "Oct 2024",
        accessed: "Feb 10, 2025",
        summary: "Annual workforce reports providing headcount, hire/separation data, and retention rates by experience cohort. All attrition percentages (12.4%, 22.7%, 30.2%) were calculated directly from these reports by tracking new-hire cohorts through subsequent years.",
        dataPoints: ["Year 1/2/3 attrition rates", "2019 vs 2024 comparison", "Cohort tracking data"],
      },
      {
        name: "Teacher Turnover Exit Survey Results",
        publisher: "NYC DOE Office of Teacher Recruitment & Quality",
        type: "primary",
        url: "https://infohub.nyced.org/",
        date: "2024",
        accessed: "Feb 12, 2025",
        summary: "Exit interview data used for the 'primary drivers' cited in this analysis (compensation, admin support, resource gaps). We reported the top three reasons as ranked by departing teachers without editorializing the findings.",
        dataPoints: ["Top departure reasons", "Ranked driver categories"],
      },
    ],
    content: `<div style="padding:16px;background:#1a0a2a;border-radius:8px;border:1px solid #3a1a4a;margin-bottom:16px"><div style="font-size:11px;color:#f59e0b;font-family:'JetBrains Mono',monospace;letter-spacing:2px;text-transform:uppercase;margin-bottom:8px">KEY FINDING</div><div style="font-size:20px;color:#ef4444;font-weight:700">30.2% attrition rate within first 3 years</div><div style="font-size:13px;color:#8b86b0;margin-top:4px">Up from 22.8% in 2019 — a 32% increase in early-career turnover</div></div><p>The data tells a clear story: NYC is hemorrhaging new teachers at an unprecedented rate.</p><p><strong style="color:#ef4444">Year 1:</strong> 12.4% leave (up from 8.1%)<br/><strong style="color:#f59e0b">Year 2:</strong> 22.7% cumulative (up from 15.3%)<br/><strong style="color:#a78bfa">Year 3:</strong> 30.2% cumulative (up from 22.8%)</p><p style="color:#8b86b0">Primary drivers: inadequate compensation, lack of administrative support, and classroom resource gaps.</p>`,
    reactions: { wise: 29, insightful: 41, fire: 18, data: 33, frustrated: 52, strong: 7 },
    comments: [
      {
        id: "c6",
        author: "f6u",
        text: "I'm in my 2nd year and already looking at other options. It's not sustainable.",
        ts: "2d ago",
        created_at: "2025-02-13T09:00:00Z",
      },
      {
        id: "c7",
        author: "g7t",
        text: "The lack of support is real. My admin hasn't visited my classroom once this year.",
        ts: "1d ago",
        created_at: "2025-02-14T09:00:00Z",
      },
    ],
  },
  {
    id: "update-1",
    type: "update",
    title: "New Tool: Compare Your Salary Across All 50 States",
    summary:
      "We built a free cost-of-living adjusted salary comparison tool. See how your NYC teacher salary stacks up when you factor in housing, taxes, and benefits.",
    tags: ["Tool", "Salary", "Wyzcon"],
    author: "wyzcon",
    timestamp: "2025-02-12T11:00:00Z",
    sources: [
      {
        name: "NEA Rankings & Estimates — Teacher Salary Data by State",
        publisher: "National Education Association",
        type: "primary",
        url: "https://www.nea.org/research-publications",
        date: "2024",
        accessed: "Feb 8, 2025",
        summary: "State-by-state average teacher salary data used as the baseline for salary comparisons in the tool. NEA is the standard source for cross-state educator compensation benchmarking.",
        dataPoints: ["Average salary by state", "2023-2024 figures"],
      },
      {
        name: "C2ER Cost of Living Index",
        publisher: "Council for Community and Economic Research",
        type: "primary",
        url: "https://www.coli.org/",
        date: "Q3 2024",
        accessed: "Feb 8, 2025",
        summary: "Cost-of-living adjustment factors used to normalize salaries across states. The tool applies composite indices covering housing, groceries, utilities, transportation, and healthcare to convert nominal salary figures into purchasing-power-equivalent values.",
        dataPoints: ["Composite COLI by metro area", "Housing sub-index"],
      },
    ],
    reactions: { wise: 15, insightful: 22, fire: 8 },
    comments: [
      {
        id: "c8",
        author: "h8s",
        text: "Tried it. Turns out I'd have 40% more purchasing power in Raleigh. Depressing.",
        ts: "5d ago",
        created_at: "2025-02-07T11:00:00Z",
      },
    ],
  },
  {
    id: "news-2",
    type: "news",
    title: "UFT Election Results: What the New Leadership Means for Your Contract",
    summary:
      "Breaking down the UFT internal election results and what the incoming leadership has signaled about priorities for the next contract negotiation cycle.",
    tags: ["UFT", "Union", "Elections"],
    author: "wyzcon",
    timestamp: "2025-02-10T16:00:00Z",
    sources: [
      {
        name: "UFT Official Election Results & Candidate Platforms",
        publisher: "United Federation of Teachers",
        type: "primary",
        url: "https://www.uft.org/",
        date: "Feb 2025",
        accessed: "Feb 10, 2025",
        summary: "Official vote tallies and winning slate information. Candidate platform documents were used to characterize the incoming leadership's stated contract negotiation priorities. All policy positions attributed to candidates are taken directly from their published platforms.",
        dataPoints: ["Vote totals", "Winning slate", "Platform priorities"],
      },
      {
        name: "UFT Election Coverage",
        publisher: "Chalkbeat New York",
        type: "secondary",
        url: "https://ny.chalkbeat.org/",
        date: "Feb 2025",
        accessed: "Feb 10, 2025",
        summary: "Independent reporting used to provide context on voter turnout, caucus dynamics, and historical comparisons to prior UFT elections. Chalkbeat's coverage supplemented official results with analysis of what the outcome signals for upcoming negotiations.",
      },
    ],
    reactions: { wise: 33, insightful: 19, frustrated: 14 },
    comments: [
      {
        id: "c9",
        author: "i9r",
        text: "Hoping the new team actually fights for real cost-of-living adjustments.",
        ts: "1w ago",
        created_at: "2025-02-03T16:00:00Z",
      },
      {
        id: "c10",
        author: "j10q",
        text: "Actions > words. Let's see what happens.",
        ts: "6d ago",
        created_at: "2025-02-04T16:00:00Z",
      },
    ],
  },
];

export const MOCK_STATS = {
  reports_published: 127,
  educators_reached: "3.2K",
  data_points: "48K+",
  active_contributors: 89,
};

export const MOCK_TRENDING = [
  { tag: "UFT Contract", count: 89 },
  { tag: "Budget Cuts", count: 67 },
  { tag: "Teacher Retention", count: 54 },
  { tag: "Class Size", count: 41 },
  { tag: "AI in Education", count: 38 },
  { tag: "Salary Analysis", count: 33 },
];

// Contract analysis data
export const CONTRACT_RAISES = {
  2009: { raise: 0, contract: "2009-2018", note: "Contract expired - no raise" },
  2010: { raise: 0, contract: "2009-2018", note: "No raise - Bloomberg years" },
  2011: { raise: 0, contract: "2009-2018", note: "No raise - 3rd year without contract" },
  2012: { raise: 0, contract: "2009-2018", note: "No raise - 4th year without contract" },
  2013: { raise: 0, contract: "2009-2018", note: "No raise - 5th year without contract" },
  2014: { raise: 2, contract: "2009-2018", note: "2014 contract ratified - 1% + 1% retro" },
  2015: { raise: 1, contract: "2009-2018", note: "1% raise + $1,000 cash bonus" },
  2016: { raise: 1.5, contract: "2009-2018", note: "1.5% raise" },
  2017: { raise: 2.5, contract: "2009-2018", note: "2.5% raise" },
  2018: { raise: 3, contract: "2009-2018", note: "3% raise - final year of 9-year deal" },
  2019: { raise: 0, contract: "2018-2021", note: "No raise - waiting for 2020 increase" },
  2020: { raise: 2.5, contract: "2018-2021", note: "2.5% raise (May 2020)" },
  2021: { raise: 3, contract: "2018-2021", note: "3% raise (May 2021)" },
  2022: { raise: 3, contract: "2022-2027", note: "3% retro raise (Sept 2022)" },
  2023: { raise: 0, contract: "2022-2027", note: "Contract ratified - retro already applied" },
  2024: { raise: 3, contract: "2022-2027", note: "3% raise (Jan 2024)" },
  2025: { raise: 3.25, contract: "2022-2027", note: "3.25% raise (Sept 2025)" },
  2026: { raise: 3.5, contract: "2022-2027", note: "3.5% raise (Sept 2026)" },
  2027: { raise: 0, contract: "2022-2027", note: "Contract expires Nov 2027" },
};

export const NYC_CPI = {
  2009: 0.3, 2010: 1.8, 2011: 2.9, 2012: 2.1, 2013: 1.5,
  2014: 1.5, 2015: 0.2, 2016: 1.1, 2017: 1.7, 2018: 2,
  2019: 1.5, 2020: 1.3, 2021: 4.2, 2022: 5.5, 2023: 3.7,
  2024: 3, 2025: 2.9, 2026: 2.9, 2027: 2.5,
};

export const SALARY_PROFILES = {
  new: { label: "New Teacher (1A, BA)", salary2009: 45530 },
  mid: { label: "Mid-Career (8B, MA+30)", salary2009: 74796 },
  senior: { label: "Senior (22yr, MA+30)", salary2009: 100049 },
};
